import { pb } from '@/libs/pocketbase'
import type { CustomerEnrichmentStatus } from '../domain/customer-enrichment'
import type { Customer, CustomerFilter, CustomerPort, CustomerUpsertInput } from '../domain/customer'

// Shape of a raw record returned by PocketBase for the customers collection.
// PocketBase field names are snake_case; the mapping to camelCase happens in toCustomer().
type PbCustomerRecord = {
	id: string
	collectionId: string
	collectionName: string
	kind: 'person' | 'company'
	name: string
	email: string
	contact: string
	phone: string
	website: string
	tax_id: string
	logo: string
	address_line1: string
	address_line2: string
	city: string
	state: string
	country: string
	country_code: string
	zip: string
	notes: string
	enrichment_status: CustomerEnrichmentStatus | '' | null
	enriched_at: string
	description: string
	industry: string
	company_type: string
	employee_count: string
	founded_year: number | null
	estimated_revenue: string
	funding_stage: string
	total_funding: string
	headquarters_location: string
	timezone: string
	linkedin_url: string
	twitter_url: string
	instagram_url: string
	facebook_url: string
	ceo_name: string
	finance_contact: string
	finance_contact_email: string
	primary_language: string
	fiscal_year_end: string
}

const toCustomer = (record: PbCustomerRecord): Customer => ({
	id: record.id,
	kind: record.kind ?? 'company',
	name: record.name || null,
	email: record.email || '',
	contact: record.contact || null,
	phone: record.phone || null,
	website: record.website || null,
	taxId: record.tax_id || null,
	logoUrl: record.logo ? pb.files.getURL(record, record.logo) : null,
	addressLine1: record.address_line1 || null,
	addressLine2: record.address_line2 || null,
	city: record.city || null,
	state: record.state || null,
	country: record.country || null,
	countryCode: record.country_code || null,
	zip: record.zip || null,
	note: record.notes || null,
	enrichmentStatus: record.enrichment_status || null,
	enrichedAt: record.enriched_at || null,
	description: record.description || null,
	industry: record.industry || null,
	companyType: record.company_type || null,
	employeeCount: record.employee_count || null,
	foundedYear: record.founded_year ?? null,
	estimatedRevenue: record.estimated_revenue || null,
	fundingStage: record.funding_stage || null,
	totalFunding: record.total_funding || null,
	headquartersLocation: record.headquarters_location || null,
	timezone: record.timezone || null,
	linkedinUrl: record.linkedin_url || null,
	twitterUrl: record.twitter_url || null,
	instagramUrl: record.instagram_url || null,
	facebookUrl: record.facebook_url || null,
	ceoName: record.ceo_name || null,
	financeContact: record.finance_contact || null,
	financeContactEmail: record.finance_contact_email || null,
	primaryLanguage: record.primary_language || null,
	fiscalYearEnd: record.fiscal_year_end || null,
})

const toFormData = (data: CustomerUpsertInput): FormData => {
	const fd = new FormData()

	fd.append('kind', data.kind)
	fd.append('name', data.name ?? '')
	fd.append('email', data.email)
	fd.append('contact', data.contact ?? '')
	fd.append('phone', data.phone ?? '')
	fd.append('website', data.website ?? '')
	fd.append('tax_id', data.taxId ?? '')
	fd.append('address_line1', data.addressLine1 ?? '')
	fd.append('address_line2', data.addressLine2 ?? '')
	fd.append('city', data.city ?? '')
	fd.append('state', data.state ?? '')
	fd.append('country', data.country ?? '')
	fd.append('country_code', data.countryCode ?? '')
	fd.append('zip', data.zip ?? '')
	fd.append('notes', data.note ?? '')

	if (data.logo instanceof File) {
		fd.append('logo', data.logo)
	} else if (data.logo === null) {
		// PocketBase clears a file field when the field name suffixed with "-" is sent empty.
		fd.append('logo-', '')
	}
	// undefined → field omitted → PocketBase keeps the existing file.

	return fd
}

const pbCustomers: CustomerPort = {
	list: async (filter: CustomerFilter) => {
		const filterExpr = filter.q ? pb.filter('name ~ {:q} || email ~ {:q} || contact ~ {:q}', { q: filter.q }) : ''
		const records = await pb.collection('customers').getFullList<PbCustomerRecord>({
			filter: filterExpr,
			sort: 'name',
			// Disable auto-cancellation: StrictMode double-invokes effects and the
			// SDK would cancel the first request when the second fires.
			requestKey: null,
		})
		return records.map(toCustomer)
	},

	getById: async (id: string) => {
		try {
			const record = await pb.collection('customers').getOne<PbCustomerRecord>(id, { requestKey: null })
			return toCustomer(record)
		} catch {
			return null
		}
	},

	upsert: async (data: CustomerUpsertInput) => {
		const fd = toFormData(data)
		if (data.id) {
			const record = await pb.collection('customers').update<PbCustomerRecord>(data.id, fd)
			return toCustomer(record)
		}
		const record = await pb.collection('customers').create<PbCustomerRecord>(fd)
		return toCustomer(record)
	},

	delete: async (id: string) => {
		await pb.collection('customers').delete(id)
	},
}

export { pbCustomers }
